import React, { useState, useMemo } from 'react';
import { Menu } from 'antd';
import { Link, useParams, useLocation } from 'react-router-dom';
import {
    LineChartOutlined,
    AppstoreOutlined,
    LinkOutlined,
    UserOutlined,
    HomeOutlined,
    BankOutlined,
    NotificationOutlined,
    GlobalOutlined,
    MailOutlined,
    CodeOutlined,
    SoundOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    CloseSquareOutlined,
    ApiOutlined,
    ControlOutlined,
    FieldTimeOutlined,
    TabletOutlined,
    DollarOutlined,
    CreditCardOutlined,
    UserSwitchOutlined,
    SafetyOutlined,
} from '@ant-design/icons';

import { getLogo, getLogoDark } from '../../utils/BrandConfig';

import '../../App.scss';
import 'antd/dist/antd.css';

const { SubMenu } = Menu;

const CLIENTS_PATHS = ['/admin/clients', '/admin/users', '/admin/tags-claimed', '/admin/team-joins', '/admin/team-leavers', '/admin/paused-clients', '/admin/live-clients'];

function getOpenKeysForPath(pathname) {
    const open = [];
    if (CLIENTS_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'))) {
        open.push('clients');
    }
    if (pathname.startsWith('/admin/withdrawals') || pathname.startsWith('/admin/batches')) {
        open.push('withdrawals');
    }
    if (pathname.startsWith('/admin/issuers')) {
        open.push('issuers');
    }
    if (pathname.startsWith('/admin/announcements')) {
        open.push('announcements');
    }
    if (pathname.startsWith('/admin/ops') || pathname.startsWith('/admin/admins')) {
        open.push('ops');
    }
    if (pathname.startsWith('/admin/customers') || pathname.startsWith('/admin/payers')) {
        open.push('customers');
    }
    return open.length > 0 ? open : ['issuers'];
}

export const AdminMenu = ({ collapsed, siderState, onToggleCollapse }) => {
    const location = useLocation();
    const { pathname } = location;
    const paramsURL = useParams();
    const { clientId, issuerId, teamId, templateId } = paramsURL;
    const [mode, setMode] = useState('inline');
    const [theme, setTheme] = useState('light');
    const [current, setCurrent] = useState(pathname);

    const pathOpenKeys = useMemo(() => getOpenKeysForPath(pathname), [pathname]);
    const [openKeys, setOpenKeys] = useState(pathOpenKeys);

    React.useEffect(() => {
        setCurrent(pathname);
        setOpenKeys(pathOpenKeys);
    }, [pathname, pathOpenKeys]);

    // Close submenus when sider collapses/hides to prevent orphaned popups
    React.useEffect(() => {
        if (collapsed || siderState === 'hidden') {
            setOpenKeys([]);
        }
    }, [collapsed, siderState]);

    const changeMode = value => {
        setMode(value ? 'vertical' : 'inline');
    };

    const changeTheme = value => {
        setTheme(value ? 'dark' : 'light');
    };

    const onClick = ({ key }) => {
        setCurrent(key);
    };

    return (
        <>
            <Menu
                selectedKeys={[current]}
                openKeys={openKeys}
                onOpenChange={setOpenKeys}
                mode={mode}
                theme={theme}
                onClick={onClick}
                inlineCollapsed={collapsed}
            >
                <Menu.Item key="/admin/logo" style={{ maxWidth: '100%', height: '50px'  }} >
                    <Link to='/admin/dashboard' style={{ textAlign: 'start', display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start' }} >
                        <img
                            src={collapsed
                                ? `${process.env.PUBLIC_URL || ''}/favicon-96x96.png`
                                : (theme === 'dark' ? getLogoDark() : getLogo())
                            }
                            style={{
                                maxWidth: collapsed ? '48px' : '140px',
                                height: collapsed ? 48 : 30,
                                objectFit: 'contain',
                                margin: '8px',
                            }}
                            alt='Home'
                        />
                    </Link>
                </Menu.Item>
                <Menu.Item key="/admin/dashboard" icon={<HomeOutlined />}>
                    <Link to='/admin/dashboard'>Home</Link>
                </Menu.Item>
                <SubMenu key="clients" icon={<UserOutlined />} title="Clients">
                    <Menu.Item key="/admin/clients" icon={<UserOutlined />}>
                        <Link to='/admin/clients'>All Clients</Link>
                    </Menu.Item>
                    <Menu.Item key="/admin/users" icon={<UserOutlined />}>
                        <Link to='/admin/users'>Users</Link>
                    </Menu.Item>
                    <Menu.Item key="/admin/tags-claimed" icon={<LinkOutlined />}>
                        <Link to='/admin/tags-claimed'>Tags Claimed</Link>
                    </Menu.Item>
                    <Menu.Item key="/admin/team-joins" icon={<LinkOutlined />}>
                        <Link to='/admin/team-joins'>Team Joins</Link>
                    </Menu.Item>
                    <Menu.Item key="/admin/team-leavers" icon={<LinkOutlined />}>
                        <Link to='/admin/team-leavers'>Team Leavers</Link>
                    </Menu.Item>
                    <Menu.Item key="/admin/paused-clients" icon={<UserOutlined />}>
                        <Link to='/admin/paused-clients'>Paused Clients</Link>
                    </Menu.Item>
                    <Menu.Item key="/admin/live-clients" icon={<UserOutlined />}>
                        <Link to='/admin/live-clients'>Live Clients</Link>
                    </Menu.Item>
                    <Menu.Item key="/admin/paytip-requests" icon={<CreditCardOutlined />}>
                        <Link to='/admin/paytip-requests'>Pay+Tip Requests</Link>
                    </Menu.Item>
                </SubMenu>
                <SubMenu key="customers" icon={<UserSwitchOutlined />} title="Customers">
                    <Menu.Item key="/admin/customers" icon={<UserSwitchOutlined />}>
                        <Link to='/admin/customers'>Live Customers</Link>
                    </Menu.Item>
                    <Menu.Item key="/admin/payers" icon={<CreditCardOutlined />}>
                        <Link to='/admin/payers'>Payers</Link>
                    </Menu.Item>
                </SubMenu>
                <Menu.Item key="/admin/balances" icon={<LineChartOutlined />}>
                    <Link to='/admin/balances'>Balances</Link>
                </Menu.Item>
                <SubMenu key="withdrawals" icon={<BankOutlined />} title="Payouts">
                    <Menu.Item key="/admin/withdrawals" icon={<BankOutlined />}>
                        <Link to='/admin/withdrawals'>Withdrawals</Link>
                    </Menu.Item>
                    <Menu.Item key="/admin/batches" icon={<BankOutlined />}>
                        <Link to='/admin/batches'>Batches</Link>
                    </Menu.Item>
                </SubMenu>
                <SubMenu key="issuers" icon={<AppstoreOutlined />} title="Templates">
                    <Menu.Item key="/admin/issuers/strike/templates">
                        <Link to='/admin/issuers/strike/templates'>Strike Templates</Link>
                    </Menu.Item>
                </SubMenu>
                <Menu.Item key="/admin/notifications" icon={<NotificationOutlined />}>
                    <Link to='/admin/notifications'>Notifications</Link>
                </Menu.Item>
                <SubMenu key="announcements" icon={<SoundOutlined />} title="Announcements">
                    <Menu.Item key="/admin/announcements" icon={<SoundOutlined />}>
                        <Link to='/admin/announcements'>Everyone</Link>
                    </Menu.Item>
                </SubMenu>
                <Menu.Item key="/admin/webhooks" icon={<CodeOutlined />}>
                    <Link to='/admin/webhooks'>Partners</Link>
                </Menu.Item>
                <Menu.Item key="/admin/sites" icon={<GlobalOutlined />}>
                    <Link to='/admin/sites'>Sites</Link>
                </Menu.Item>
                <SubMenu key="ops" icon={<ControlOutlined />} title="Ops">
                    <Menu.Item key="/admin/ops/processors" icon={<DollarOutlined />}>
                        <Link to='/admin/ops/processors'>Processors</Link>
                    </Menu.Item>
                    <Menu.Item key="/admin/admins" icon={<SafetyOutlined />}>
                        <Link to='/admin/admins'>Strike Admins</Link>
                    </Menu.Item>
                    <Menu.Item key="/admin/ops/crons" icon={<FieldTimeOutlined />}>
                        <Link to='/admin/ops/crons'>Crons</Link>
                    </Menu.Item>
                    <Menu.Item key="/admin/ops/terminals" icon={<TabletOutlined />}>
                        <Link to='/admin/ops/terminals'>Terminals</Link>
                    </Menu.Item>
                    <Menu.Item key="/admin/ops/integrations" icon={<ApiOutlined />}>
                        <Link to='/admin/ops/integrations'>Integrations</Link>
                    </Menu.Item>
                </SubMenu>
                <Menu.Item key="link" icon={<LinkOutlined />}>
                    <a href="https://strikepay.co" target="_blank" rel="noopener noreferrer">
                        Strikepay.co
                    </a>
                </Menu.Item>
                {onToggleCollapse && (
                    <Menu.Item
                        key="collapse"
                        icon={
                            (!siderState || siderState === 'expanded')
                                ? <MenuFoldOutlined />
                                : siderState === 'collapsed'
                                    ? <CloseSquareOutlined />
                                    : <MenuUnfoldOutlined />
                        }
                        onClick={onToggleCollapse}
                    >
                        {(!siderState || siderState === 'expanded') ? 'Shrink' : siderState === 'collapsed' ? 'Hide' : 'Expand'}
                    </Menu.Item>
                )}
            </Menu>
        </>
    );
};